import React, { useMemo } from 'react';
import {
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Button,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { selectDeploymentWarnings, dismissWarning } from '../../slices/deploymentTrackingSlice';

interface DeploymentWarningBannerProps {
  /**
   * When provided, only warnings for this tenant are shown (tenant-scoped views:
   * View 1 Tenant Labels, View 2 Tenant Policies). When omitted, all warnings are
   * shown (dashboard views: View 3 Dashboard Labels, View 4 Dashboard Policies).
   */
  tenantId?: string;
}

/**
 * Shared yellow, non-blocking, dismissible warning banner surfaced above the table
 * in all four deployment views. Renders prefix-skip / not-updated warnings emitted by
 * the Deployment Engine (for labels left unprefixed AND policies reused under a
 * different prefix) and plumbed through the deploymentTracking slice.
 */
export const DeploymentWarningBanner: React.FC<DeploymentWarningBannerProps> = ({ tenantId }) => {
  const dispatch = useAppDispatch();
  const allWarnings = useAppSelector(selectDeploymentWarnings);

  const warnings = useMemo(() => {
    if (!tenantId) return allWarnings;
    return allWarnings.filter(w => w.tenantId?.toLowerCase() === tenantId.toLowerCase());
  }, [allWarnings, tenantId]);

  if (warnings.length === 0) return null;

  return (
    <MessageBar intent="warning" style={{ marginBottom: 12 }}>
      <MessageBarBody>
        <MessageBarTitle>Some items were not updated</MessageBarTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
          {warnings.map(w => (
            <div key={w.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ flex: 1 }}>
                <strong>{w.label || 'Item'}:</strong> {w.message}
              </span>
              <Button
                appearance="subtle"
                size="small"
                icon={<Dismiss24Regular />}
                onClick={() => dispatch(dismissWarning(w.id))}
                aria-label={`dismiss warning for ${w.label || 'item'}`}
              />
            </div>
          ))}
        </div>
      </MessageBarBody>
      {warnings.length > 1 && (
        <Button
          appearance="subtle"
          size="small"
          onClick={() => warnings.forEach(w => dispatch(dismissWarning(w.id)))}
          aria-label="dismiss all warnings"
        >
          Dismiss all
        </Button>
      )}
    </MessageBar>
  );
};

export default DeploymentWarningBanner;
